import React from "react";
import { Button, makeStyles, shorthands } from "@fluentui/react-components";
import { Report } from "../../../types/reportTypes";
import FormField from "./FormField";
import PopupTable from "./PopupTable";
import { useReportForm } from "../hooks/useReportForm";
import { downloadReport } from "../serviceFile";
import { transformReportName } from "../../../utils";
import moment from "moment";
import "../style.css";

const useStyles = makeStyles({
  formField: {
    display: "flex",
    alignItems: "baseline",
    flexWrap: "wrap",
    gap: "10px",
  },
  buttonContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "end",
  },
});

interface ReportFormProps {
  selectedReport: Report;
  onReportGenerated: () => void;
  showToast: (title: string, message: string, type: string | undefined) => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  // Values merged into the submit payload without ever being rendered as a
  // field — e.g. the client-context Reports screen injects `customerId` (and
  // `startDate`/`endDate` when the report doesn't declare its own) so the
  // "Select Client" popup never has to appear. Omitted on the standalone
  // /report page, so its behavior is unchanged.
  fixedParams?: Record<string, any>;
}

// Helper function to safely get nested values from objects
const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

const ReportForm: React.FC<ReportFormProps> = ({
  selectedReport,
  onReportGenerated,
  showToast,
  setIsLoading,
  fixedParams,
}) => {
  const styles = useStyles();
  const {
    formValues,
    errors,
    popupState,
    dropdownOptions,
    loadingFields,
    handleInputChange,
    handleBlur,
    validateField,
    setPopupState,
    onLoadMore,
  } = useReportForm(selectedReport);

  const handleSubmit = async () => {
    const newErrors: { [key: string]: string } = {};
    selectedReport.parameters.forEach((param) => {
      const error = validateField(param, formValues[param.label]);
      if (error) newErrors[param.label] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const params: Record<string, any> = {};
    let selectedClientLastName = "";
    let configErrorMessage: string | null = null;

    selectedReport.parameters.forEach((param) => {
      const value = formValues[param.label];
      if (!value) return;

      let finalValue = value;

      if (param.category === "select") {
        if (param.popup) {
          if (param.multiSelect) {
            // Handle multi-select popup
            if (param.apiEndpoint!.value.includes(".")) {
              finalValue = Array.isArray(value)
                ? value.map((item: any) =>
                    getNestedValue(item, param.apiEndpoint!.value)
                  )
                : value
                ? [getNestedValue(value, param.apiEndpoint!.value)]
                : [];
            } else {
              finalValue = Array.isArray(value)
                ? value.map((item: any) => item[param.apiEndpoint!.value])
                : value
                ? [value[param.apiEndpoint!.value]]
                : [];
            }
          } else {
            // Handle single select popup
            if (param.apiEndpoint!.value.includes(".")) {
              finalValue =
                getNestedValue(value, param.apiEndpoint!.value) || "";
            } else {
              finalValue = value?.[param.apiEndpoint!.value] || "";
            }
          }
          if (param.label === "Client List") {
            selectedClientLastName = value?.last_name || "";
          }
        } else {
          // Handle regular dropdown
          if (param.multiSelect) {
            finalValue = Array.isArray(value)
              ? value.map((item: any) => item.value)
              : value?.value
              ? [value.value]
              : [];
          } else {
            finalValue = value?.value || "";
          }
        }
      } else if (param.category === "datepicker") {
        finalValue = value;
      } else if (param.category === "daterange") {
        // Skip incomplete optional ranges instead of sending empty-string
        // dates — matches how every other optional field is treated above.
        if (!value?.startDate || !value?.endDate) return;

        const [startKey, endKey] = (param.reportParam || "")
          .split(",")
          .map((k) => k.trim());
        if (!startKey || !endKey) {
          console.error(
            `Report parameter "${param.label}" has category "daterange" but reportParam is not a valid "startKey,endKey" pair: "${param.reportParam}"`
          );
          configErrorMessage = `"${param.label}" is misconfigured and could not be included in the report. Please contact support.`;
          return;
        }
        params[startKey] = value.startDate;
        params[endKey] = value.endDate;
        return;
      }

      const key = param.reportParam;
      if (key) {
        params[key] = finalValue;
      }
    });

    if (configErrorMessage) {
      showToast("Report configuration error", configErrorMessage, "error");
      return;
    }

    // Merge in values that were never rendered as fields at all (e.g.
    // customerId/startDate/endDate supplied by the client-context Reports
    // screen). No-op when fixedParams is undefined, so the standalone
    // /report page's payload is byte-for-byte unchanged.
    Object.assign(params, fixedParams);

    try {
      setIsLoading(true);
      const blob = await downloadReport(
        selectedReport.ReportCode,
        selectedReport.ContentType as "pdf" | "xlsx" | "docx",
        selectedReport.scope,
        params
      );

      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const extension =
          selectedReport.ContentType === "pdf"
            ? "pdf"
            : selectedReport.ContentType === "docx"
            ? "docx"
            : "xlsx";

        const transformedReportName = transformReportName(selectedReport.Name);

        link.download = selectedClientLastName
          ? `${selectedClientLastName}_${moment().format(
              "YYYYMMDD"
            )}_${transformedReportName}.${extension}`
          : `${moment().format(
              "YYYYMMDD"
            )}_${transformedReportName}.${extension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToast("Report generated successfully!", "", "success");
        setIsLoading(false);
        onReportGenerated();
      } else {
        showToast("Failed to download the report.", "", "error");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      showToast("An error occurred while downloading the report.", "", "error");
    }
  };

  const handleOpenPopup = (paramLabel: string) => {
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        isOpen: true,
      },
    }));
  };

  const handleClosePopup = (paramLabel: string) => {
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        isOpen: false,
        rowSelection: {},
        selected: null,
      },
    }));
  };

  const handleSelectFromPopup = (paramLabel: string, selected: any) => {
    handleInputChange(paramLabel, selected);
    handleClosePopup(paramLabel);
  };

  const handleRowSelectionChange = (paramLabel: string, updater: any) => {
    setPopupState((prev: any) => {
      const currentData = prev[paramLabel]?.data || [];
      const currentSelection = prev[paramLabel]?.rowSelection || {};
      const newRowSelection =
        typeof updater === "function" ? updater(currentSelection) : updater;
      let selectedItems = null;

      const param = selectedReport.parameters.find(
        (p) => p.label === paramLabel
      );
      if (param?.multiSelect) {
        // Handle multi-select
        const selectedIndices = Object.keys(newRowSelection).filter(
          (key) => newRowSelection[key]
        );
        selectedItems = selectedIndices.map(
          (index) => currentData[parseInt(index)]
        );
      } else {
        // Handle single select
        const selectedIndex = Object.keys(newRowSelection)[0];
        selectedItems = selectedIndex
          ? currentData[parseInt(selectedIndex)]
          : null;
      }

      return {
        ...prev,
        [paramLabel]: {
          ...prev[paramLabel],
          rowSelection: newRowSelection,
          selected: selectedItems,
        },
      };
    });
  };

  const handlePaginationChange = (paramLabel: string, updater: any) => {
    const newPagination =
      typeof updater === "function"
        ? updater(
            popupState[paramLabel]?.pagination || {
              pageIndex: 0,
              pageSize: 10,
            }
          )
        : updater;
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        pagination: newPagination,
        isOpen: true,
      },
    }));
  };

  const handleGlobalFilterChange = (paramLabel: string, value: string) => {
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        searchQuery: value || "",
        pagination: {
          ...prev[paramLabel]?.pagination,
          pageIndex: 0,
        },
      },
    }));
  };
  const isFormValid = React.useMemo(() => {
    // If any error exists, form is invalid
    if (Object.values(errors).some((msg) => msg)) return false;

    // Ensure required fields are filled
    return selectedReport.parameters.every((param) => {
      if (param.required) {
        const value = formValues[param.label];
        if (param.multiSelect) return Array.isArray(value) && value.length > 0;
        if (param.category === "daterange") {
          return Boolean(value?.startDate && value?.endDate);
        }
        return Boolean(value);
      }
      return true;
    });
  }, [errors, formValues, selectedReport.parameters]);

  return (
    <div className="report_selection">
      <div className={styles.formField}>
        {selectedReport.parameters.map((param) => (
          <div key={param.label}>
            <FormField
              param={param}
              value={formValues[param.label]}
              error={errors[param.label]}
              loading={loadingFields[param.label]}
              dropdownOptions={dropdownOptions[param.label] || []}
              onInputChange={handleInputChange}
              onBlur={handleBlur}
              onOpenPopup={handleOpenPopup}
            />
            {param.popup && (
              <PopupTable
                param={param}
                popupState={
                  popupState[param.label] || {
                    data: [],
                    selected: null,
                    total: 0,
                    isOpen: false,
                    pagination: { pageIndex: 0, pageSize: param.apiEndpoint?.QueryParams?.includes("offset") ? 20 : 10 },
                    searchQuery: "",
                    rowSelection: {},
                    isFetchingMore: false,
                  }
                }
                onClose={() => handleClosePopup(param.label)}
                onSelect={(selected) =>
                  handleSelectFromPopup(param.label, selected)
                }
                onRowSelectionChange={(updater) =>
                  handleRowSelectionChange(param.label, updater)
                }
                onPaginationChange={(updater) =>
                  handlePaginationChange(param.label, updater)
                }
                onGlobalFilterChange={(value) =>
                  handleGlobalFilterChange(param.label, value)
                }
                useScrollPagination={
                  param.apiEndpoint?.QueryParams?.includes("offset") ?? false
                }
                onLoadMore={() => onLoadMore(param.label)}
              />
            )}
          </div>
        ))}
      </div>
      <div
        className={styles.buttonContainer}
        style={{
          alignItems: Object.values(errors).every((msg) => !msg)
            ? "end"
            : "center",
        }}
      >
        <Button
          appearance="primary"
          onClick={handleSubmit}
          className="asc-button-primary"
          disabled={!isFormValid}
        >
          Generate Report
        </Button>
      </div>
    </div>
  );
};

export default ReportForm;
